import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useInfiniteScroll } from "_shared/_hooks/infinite-scroll/use-infinite-scroll";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { useMemo, useRef } from "react";
import UserCoworker from "users/information/coworker-information/user-coworker";
import UserInformation from "users/information/user-information";

const MainContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));
const MainContent = styled("div")(() => ({
  display: "flex",
  flex: 1,
  height: "100%",
  overflowY: "auto",
}));
const StyledCheckbox = styled("input")(({ theme }) => ({
  margin: theme.spacing(1),
  accentColor: theme.palette.primary.main,
}));
const StyledRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  transition: "background 0.2s ease",
  cursor: "pointer",
  "&:hover": {
    background: theme.palette.action.hover,
  },
}));
const LoadingContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
}));

type SelectableNetworkMember = {
  userId: Guid;
  fullName: string;
  type: string;
};

type NetworkedGroupMarketplaceProps = {
  coworkers: UserCoworker[];
  networkConnections: UserInformation[];
  selectedMemberUserIds: Guid[];
  coworkerHasNextPage?: boolean;
  isLoadingCoworkers?: boolean;
  coworkerError?: unknown;
  onLoadMoreCoworkers?: () => void;
  onRetryCoworkers?: () => void;
  onMemberSelected?: (memberUserId: Guid) => void;
};

export default function NetworkedGroupSelector(props: Readonly<NetworkedGroupMarketplaceProps>) {
  const { coworkers, networkConnections, selectedMemberUserIds, coworkerError, onRetryCoworkers, onMemberSelected } = props;
  const coworkerHasNextPage = props.coworkerHasNextPage ?? false;
  const isLoadingCoworkers = props.isLoadingCoworkers ?? false;
  const onLoadMoreCoworkers = props.onLoadMoreCoworkers ?? (() => {});
  const contentRef = useRef<HTMLDivElement>(null);
  useInfiniteScroll(
    onLoadMoreCoworkers,
    coworkerHasNextPage,
    isLoadingCoworkers,
    contentRef,
  );

  const selectableMembers = useMemo(() => {
    const members: SelectableNetworkMember[] = [
      ...coworkers.map(coworker => ({
        userId: coworker.userId,
        fullName: coworker.name.fullName,
        type: "coworker"
      })),
      ...networkConnections.map(connection => ({
        userId: connection.id,
        fullName: connection.name.fullName,
        type: "networkConnection"
      })),
      ...selectedMemberUserIds.map(userId => ({
        userId,
        fullName: "Unavailable member",
        type: "unavailable",
      })),
    ];
    return _.uniqBy(members, (members => members.userId.value));
  }, [coworkers, networkConnections, selectedMemberUserIds]);

  return (
    <MainContainer>
      <MainContent ref={contentRef}>
        <div>
          {selectableMembers.map(member => (
            <StyledRow
              key={member.userId.value}
              onClick={() =>
                (member.type !== "unavailable" || selectedMemberUserIds.some(id => id.isEqualTo(member.userId))) &&
                onMemberSelected?.(member.userId)
              }
            >
              <StyledCheckbox
                type="checkbox"
                checked={selectedMemberUserIds.some(i => i.isEqualTo(member.userId))}
                disabled={member.type === "unavailable" && !selectedMemberUserIds.some(id => id.isEqualTo(member.userId))}
                onChange={() => onMemberSelected?.(member.userId)}
                onClick={(e) => e.stopPropagation()}
              />
              <Tooltip title={
                member.type === "unavailable"
                  ? "This member is no longer available in your current source lists."
                  : member.type === "coworker" ? "Coworker" : "Network"
              }>
                <Typography>{member.fullName}</Typography>
              </Tooltip>
            </StyledRow>
          ))}
          {isLoadingCoworkers && (
            <LoadingContainer>
              <CircularProgress aria-label="Loading more coworkers" size={24} />
            </LoadingContainer>
          )}
          {Boolean(coworkerError) && (
            <Alert
              action={
                onRetryCoworkers ? (
                  <Button color="inherit" size="small" onClick={onRetryCoworkers}>
                    Retry
                  </Button>
                ) : undefined
              }
              severity="error"
            >
              Failed to load coworkers.
            </Alert>
          )}
        </div>
      </MainContent>
    </MainContainer>
  );
}
